import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  useTheme,
  useMediaQuery,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Container,
  Slide,
  Fade,
  Badge,
  Avatar,
  Menu,
  MenuItem,
  Chip
} from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import LanguageSelector from '../Common/LanguageSelector';

import QueryIcon from '@mui/icons-material/Search';
import DocumentIcon from '@mui/icons-material/Description';
import BulkQueryIcon from '@mui/icons-material/DynamicFeed';
import SettingsIcon from '@mui/icons-material/Settings';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import HomeIcon from '@mui/icons-material/Home';
import BiotechIcon from '@mui/icons-material/Biotech';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';

const Header = () => {
  const theme = useTheme();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);

  // Navigation items
  const navItems = [
    { text: t('navigation.home'), icon: <HomeIcon />, path: '/' },
    { text: t('navigation.query'), icon: <QueryIcon />, path: '/query' },
    { text: t('navigation.orchestration'), icon: <AutoAwesomeIcon />, path: '/orchestration' },
    { text: t('navigation.documents'), icon: <DocumentIcon />, path: '/documents' },
    { text: t('navigation.bulkQuery'), icon: <BulkQueryIcon />, path: '/bulk-query' },
    { text: t('navigation.settings'), icon: <SettingsIcon />, path: '/settings' },
  ];

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    setMounted(true);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const handleUserMenuOpen = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleLogout = async () => {
    await logout();
    handleUserMenuClose();
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin':
        return 'error';
      case 'researcher':
        return 'warning';
      default:
        return 'primary';
    }
  };

  // Drawer content
  const drawerContent = (
    <Box
      sx={{
        width: 280,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        p: 2,
      }}
      role="presentation"
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Avatar
            src="/Neobot-logo.png"
            sx={{
              bgcolor: 'primary.main',
              mr: 1,
              '& img': {
                objectFit: 'contain'
              }
            }}
          >
            <BiotechIcon />
          </Avatar>
          <Typography variant="h6" color="primary.main" fontWeight="bold">
            {t('header.title')}
          </Typography>
        </Box>
        <IconButton onClick={toggleDrawer} size="large" edge="end">
          <CloseIcon />
        </IconButton>
      </Box>

      <Divider sx={{ mb: 2 }} />

      <List>
        {navItems.map((item) => (
          <ListItem
            button
            key={item.text}
            component={RouterLink}
            to={item.path}
            onClick={toggleDrawer}
            sx={{
              borderRadius: 2,
              mb: 1,
              bgcolor: isActive(item.path) ? 'primary.light' : 'transparent',
              color: isActive(item.path) ? 'primary.contrastText' : 'text.primary',
              '&:hover': {
                bgcolor: isActive(item.path) ? 'primary.light' : 'action.hover',
              },
            }}
          >
            <ListItemIcon sx={{ color: isActive(item.path) ? 'primary.contrastText' : 'primary.main', minWidth: 40 }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText primary={item.text} />
          </ListItem>
        ))}
      </List>

      <Box sx={{ flexGrow: 1 }} />

      {/* Language Selector in Drawer */}
      <Box sx={{ px: 2, py: 1 }}>
        <LanguageSelector variant="button" />
      </Box>

      {/* User Info in Drawer */}
      <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
            <PersonIcon />
          </Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle2" fontWeight="bold">
              {user?.full_name || user?.email}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                label={user?.role}
                size="small"
                color={getRoleColor(user?.role)}
                variant="outlined"
              />
            </Box>
          </Box>
        </Box>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<LogoutIcon />}
          onClick={handleLogout}
          size="small"
        >
          {t('common.logout')}
        </Button>
      </Box>

      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          {t('header.version')}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <>
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={toggleDrawer}
        sx={{
          '& .MuiDrawer-paper': {
            borderRadius: '12px 0 0 12px',
            boxShadow: '-4px 0 20px rgba(0,0,0,0.1)',
          },
        }}
      >
        {drawerContent}
      </Drawer>

      <Slide appear={false} direction="down" in={!scrolled || !mounted}>
        <AppBar
          position="sticky"
          elevation={scrolled ? 4 : 1}
          sx={{
            bgcolor: scrolled ? 'rgba(255, 255, 255, 0.95)' : 'background.paper',
            backdropFilter: scrolled ? 'blur(8px)' : 'none',
            transition: 'all 0.3s ease',
          }}
        >
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ height: scrolled ? 64 : 70, transition: 'height 0.3s ease' }}>
            <Fade in={mounted} timeout={800}>
              <Typography
                variant="h6"
                component={RouterLink}
                to="/"
                sx={{
                  flexGrow: 1,
                  textDecoration: 'none',
                  color: scrolled ? 'primary.main' : 'inherit',
                  display: 'flex',
                  alignItems: 'center',
                  fontWeight: 'bold',
                  letterSpacing: '0.5px',
                  transition: 'color 0.3s ease',
                }}
              >
                <Badge
                  overlap="circular"
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  badgeContent={
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: 'success.main',
                        border: '2px solid white',
                      }}
                    />
                  }
                >
                  <Avatar
                    src="/Neobot-logo.png"
                    sx={{
                      bgcolor: 'primary.main',
                      mr: 1,
                      width: scrolled ? 36 : 40,
                      height: scrolled ? 36 : 40,
                      transition: 'all 0.3s ease',
                      '& img': {
                        objectFit: 'contain'
                      }
                    }}
                  >
                    <BiotechIcon />
                  </Avatar>
                </Badge>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography
                    variant={scrolled ? "subtitle1" : "h6"}
                    component="span"
                    sx={{
                      fontWeight: 'bold',
                      lineHeight: 1.2,
                      transition: 'all 0.3s ease',
                    }}
                  >
                    {t('header.title')}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: { xs: 'none', sm: 'block' },
                      opacity: scrolled ? 0.7 : 1,
                      transition: 'opacity 0.3s ease',
                    }}
                  >
                    {t('header.subtitle')}
                  </Typography>
                </Box>
              </Typography>
            </Fade>

            {isMobile ? (
              <IconButton
                color={scrolled ? "primary" : "inherit"}
                aria-label="open drawer"
                edge="end"
                onClick={toggleDrawer}
                sx={{ ml: 1 }}
              >
                <MenuIcon />
              </IconButton>
            ) : (
              <Fade in={mounted} timeout={1000}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  {navItems.map((item, index) => (
                    item.path === '/' ? (
                      <Tooltip key={item.text} title={t('navigation.home')} arrow>
                        <IconButton
                          component={RouterLink}
                          to={item.path}
                          color={isActive(item.path) ? "primary" : "inherit"}
                          sx={{
                            transition: 'all 0.2s ease',
                            transform: isActive(item.path) ? 'scale(1.1)' : 'scale(1)',
                          }}
                        >
                          {item.icon}
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Button
                        key={item.text}
                        color={scrolled ? (isActive(item.path) ? "primary" : "inherit") : "inherit"}
                        variant={isActive(item.path) ? "contained" : "text"}
                        component={RouterLink}
                        to={item.path}
                        startIcon={item.icon}
                        sx={{
                          borderRadius: '20px',
                          px: 2,
                          py: 0.8,
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: isActive(item.path) ? 4 : 0,
                          },
                        }}
                      >
                        {item.text}
                      </Button>
                    )
                  ))}

                  {/* Language Selector */}
                  <LanguageSelector variant="button" color={scrolled ? "primary" : "inherit"} />

                  {/* User Menu */}
                  <Box sx={{ ml: 2 }}>
                    <Tooltip title={t('navigation.account')}>
                      <IconButton
                        onClick={handleUserMenuOpen}
                        color={scrolled ? "primary" : "inherit"}
                        sx={{ p: 0 }}
                      >
                        <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                          <PersonIcon />
                        </Avatar>
                      </IconButton>
                    </Tooltip>
                    <Menu
                      anchorEl={userMenuAnchor}
                      open={Boolean(userMenuAnchor)}
                      onClose={handleUserMenuClose}
                      onClick={handleUserMenuClose}
                      PaperProps={{
                        elevation: 3,
                        sx: {
                          overflow: 'visible',
                          filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                          mt: 1.5,
                          minWidth: 200,
                          '& .MuiAvatar-root': {
                            width: 32,
                            height: 32,
                            ml: -0.5,
                            mr: 1,
                          },
                        },
                      }}
                      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                    >
                      <Box sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {user?.full_name || user?.email}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {user?.email}
                        </Typography>
                        <Box sx={{ mt: 0.5 }}>
                          <Chip
                            label={user?.role}
                            size="small"
                            color={getRoleColor(user?.role)}
                            variant="outlined"
                          />
                        </Box>
                      </Box>
                      <MenuItem onClick={handleLogout}>
                        <LogoutIcon sx={{ mr: 1 }} />
                        {t('common.logout')}
                      </MenuItem>
                    </Menu>
                  </Box>
                </Box>
              </Fade>
            )}
          </Toolbar>
        </Container>
      </AppBar>
    </Slide>
    </>
  );
};

// Add a Divider component
const Divider = ({ sx = {} }) => (
  <Box
    sx={{
      height: '1px',
      width: '100%',
      bgcolor: 'divider',
      ...sx,
    }}
  />
);

export default Header;
